import { HEIGHT, WIDTH } from "./engine.js";
import { drawFlightRecords } from "./flight-records.js";

const ROOT = "/assets/night-flight-illustrated/";
const ART_TIMEOUT_MS = 15000;
export const FLIGHT_ART = Object.freeze({
  background: `${ROOT}castle-portrait.webp`, panorama: `${ROOT}castle-panorama.webp`,
  witch: `${ROOT}witch.webp`, column: `${ROOT}column.webp`, pumpkin: `${ROOT}pumpkin.webp`,
});

/** Browser caching shares WebP resources with the lobby illustration. */
export async function loadFlightArt() {
  const entries = await Promise.all(Object.entries(FLIGHT_ART).filter(([key]) => key !== "panorama").map(async ([key, url]) => {
    const image = new Image(); image.decoding = "async"; image.src = url;
    let timer;
    try {
      await Promise.race([image.decode(), new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("Flight artwork timed out")), ART_TIMEOUT_MS);
      })]);
      return [key, image];
    } catch (error) {
      image.src = "";
      throw error;
    } finally { clearTimeout(timer); }
  }));
  return Object.fromEntries(entries);
}

function cover(ctx, image, width, height) {
  const scale = Math.max(width / image.width, height / image.height);
  const sw = width / scale, sh = height / scale;
  ctx.drawImage(image, (image.width - sw) / 2, (image.height - sh) / 2, sw, sh, 0, 0, width, height);
}

/** Keep windows upright and mark the exact collision edge with the luminous cap. */
function pillar(ctx, image, x, edge, upper) {
  const width = 60, capHeight = 19, sourceCap = Math.round(image.height * .08);
  const bodyHeight = (image.height - sourceCap) * width / image.width;
  const top = upper ? 0 : edge, height = upper ? edge : HEIGHT - edge;
  if (height <= 0) return;
  ctx.save(); ctx.beginPath(); ctx.rect(x - width / 2, top, width, height); ctx.clip();
  ctx.fillStyle = "#251c38"; ctx.fillRect(x - width / 2, top, width, height);
  const start = upper ? edge - capHeight - bodyHeight : edge + capHeight;
  for (let y = start; upper ? y + bodyHeight > 0 : y < HEIGHT; y += upper ? -bodyHeight : bodyHeight) {
    ctx.drawImage(image, 0, sourceCap, image.width, image.height - sourceCap, x - width / 2, y, width, bodyHeight);
  }
  if (upper) {
    ctx.translate(x, edge); ctx.scale(1, -1);
    ctx.drawImage(image, 0, 0, image.width, sourceCap, -width / 2, 0, width, capHeight);
  } else ctx.drawImage(image, 0, 0, image.width, sourceCap, x - width / 2, edge, width, capHeight);
  ctx.restore();
  ctx.fillStyle = "#ffd693"; ctx.fillRect(x - width / 2, upper ? edge - 1 : edge, width, 1);
}

/** Locate the visible interval in a sorted course without scanning passed/future gates. */
export function visibleFlightGates(gates, distance) {
  const left = distance - 65000, right = distance + (WIDTH + 65) * 1000;
  let low = 0, high = gates.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (gates[middle].x < left) low = middle + 1;
    else high = middle;
  }
  const first = low;
  while (low < gates.length && gates[low].x <= right) low++;
  return [first, low];
}

/** Reuse invariant paint resources; let the browser cache decoded image textures. */
export function createFlightRenderer(ctx, art, backgroundContext = null) {
  if (backgroundContext) cover(backgroundContext, art.background, WIDTH, HEIGHT);
  const ground = ctx.createLinearGradient(0, HEIGHT - 16, 0, HEIGHT);
  ground.addColorStop(0, "#19102d00"); ground.addColorStop(1, "#19102d");
  return (state, gates, daily, dailyLimit, practice, reducedMotion, records = []) =>
    drawFlightScene(ctx, state, gates, daily, dailyLimit, practice, reducedMotion, art, records, ground, !backgroundContext);
}

export function drawFlightScene(ctx, state, gates, daily, dailyLimit, practice, reducedMotion, art, records = [], ground = null, drawBackground = true) {
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
  if (drawBackground) cover(ctx, art.background, WIDTH, HEIGHT);
  else ctx.clearRect(0, 0, WIDTH, HEIGHT);
  const [first, end] = visibleFlightGates(gates, state.distance);
  for (let index = first; index < end; index++) {
    const gate = gates[index];
    const x = (gate.x - state.distance) / 1000;
    pillar(ctx, art.column, x, (gate.center - gate.gap / 2) / 1000, true);
    pillar(ctx, art.column, x, (gate.center + gate.gap / 2) / 1000, false);
    if (gate.item && !state.collected.includes(gate.id) && (practice || daily < dailyLimit)) {
      const itemX = ((gate.item_x ?? gate.x) - state.distance) / 1000;
      const itemY = (gate.item_y ?? gate.center) / 1000;
      const size = 34;
      ctx.drawImage(art.pumpkin, itemX - size / 2, itemY - size / 2, size, size);
    }
  }
  drawFlightRecords(ctx, records, state.distance, WIDTH, HEIGHT);
  const y = state.y / 1000;
  if (!reducedMotion) {
    for (let i = 0; i < 3; i++) {
      ctx.globalAlpha = (1 - i / 3) * .35; ctx.fillStyle = "#ffd28e";
      ctx.fillRect(35 - i * 8, y + 9, 2, 2);
    }
    ctx.globalAlpha = 1;
  }
  ctx.save(); ctx.translate(90, y); ctx.rotate(Math.max(-.12, Math.min(.12, state.velocity / 26000)));
  const witchWidth = 100, witchHeight = witchWidth * art.witch.height / art.witch.width;
  ctx.drawImage(art.witch, -65, -witchHeight * .68, witchWidth, witchHeight); ctx.restore();
  if (!ground) {
    ground = ctx.createLinearGradient(0, HEIGHT - 16, 0, HEIGHT);
    ground.addColorStop(0, "#19102d00"); ground.addColorStop(1, "#19102d");
  }
  ctx.fillStyle = ground; ctx.fillRect(0, HEIGHT - 16, WIDTH, 16);
}
